import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  BlockStack,
  FormLayout,
  TextField,
  Select,
  Banner,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useHealthcareCompanyMeta } from "@shared/hooks/common/useHealthcareCompanyMeta";
import useApiRequest from "@shared/hooks/common/useApiRequest";

interface TaxNumberComponentProps {
  taxNumber: string;
  taxType: string;
  errors: Record<string, boolean>;
  handleInputChange: (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string
  ) => void;
  setTaxNumber: React.Dispatch<React.SetStateAction<string>>;
  setTaxType: React.Dispatch<React.SetStateAction<string>>;
  disabled?: boolean;
}

interface ValidationState {
  isValidating: boolean;
  isValid: boolean | null;
  message: string | null;
  error: string | null;
}

const initialValidationState: ValidationState = {
  isValidating: false,
  isValid: null,
  message: null,
  error: null,
};

const TaxNumberComponent: React.FC<TaxNumberComponentProps> = ({
  taxNumber,
  taxType,
  errors,
  handleInputChange,
  setTaxNumber,
  setTaxType,
  disabled,
}) => {
  const { t } = useTranslation();
  const { taxTypes, isLoading: isMetaLoading } = useHealthcareCompanyMeta();
  const { POST } = useApiRequest();
  const postRef = useRef(POST);
  const lastValidationKeyRef = useRef<string | null>(null);
  const activeValidationAbortRef = useRef<AbortController | null>(null);
  const validationRequestSeqRef = useRef(0);
  
  const [validationState, setValidationState] =
    useState<ValidationState>(initialValidationState);

  useEffect(() => {
    postRef.current = POST;
  }, [POST]);

  const taxTypeOptions = taxTypes.map((taxType) => ({
    label: taxType.label,
    value: taxType.id,
  }));

  const validateTaxNumber = useCallback(
    async (taxType: string, taxNumber: string) => {
      if (!taxType || !taxNumber.trim()) {
        activeValidationAbortRef.current?.abort();
        setValidationState(initialValidationState);
        return;
      }

      activeValidationAbortRef.current?.abort();
      const abortController = new AbortController();
      activeValidationAbortRef.current = abortController;
      const requestSeq = validationRequestSeqRef.current + 1;
      validationRequestSeqRef.current = requestSeq;

      setValidationState({
        isValidating: true,
        isValid: null,
        message: null,
        error: null,
      });

      try {
        const response = await postRef.current<{
          status: string;
          companyDetails: {
            name: string;
            country: string;
            address: string;
          };
          message: string;
        }>("/api/billing/validate-tax", {
          taxType,
          taxNumber,
        }, false, true, true, abortController.signal);

        if (requestSeq !== validationRequestSeqRef.current) {
          return;
        }

        setValidationState({
          isValidating: false,
          isValid: response.status === "valid",
          message: response.message,
          error: null,
        });
      } catch (error) {
        const maybeCanceled = error as { code?: string };
        if (maybeCanceled.code === "ERR_CANCELED") {
          return;
        }

        if (requestSeq !== validationRequestSeqRef.current) {
          return;
        }

        const errorResponse = error as {
          response?: { data?: { error?: { display?: string } } };
        };
        const errorMessage =
          errorResponse?.response?.data?.error?.display ??
          t("auth.common.errorOccurred");
        setValidationState({
          isValidating: false,
          isValid: false,
          message: null,
          error: errorMessage,
        });
      } finally {
        if (activeValidationAbortRef.current === abortController) {
          activeValidationAbortRef.current = null;
        }
      }
    },
    [t]
  );

  useEffect(() => {
    if (disabled) {
      activeValidationAbortRef.current?.abort();
      return;
    }

    const normalizedTaxNumber = taxNumber.trim();
    if (!taxType || !normalizedTaxNumber) {
      activeValidationAbortRef.current?.abort();
      lastValidationKeyRef.current = null;
      setValidationState(initialValidationState);
      return;
    }

    const validationKey = `${taxType}:${normalizedTaxNumber}`;
    if (validationKey === lastValidationKeyRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      lastValidationKeyRef.current = validationKey;
      validateTaxNumber(taxType, normalizedTaxNumber);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [taxType, taxNumber, disabled, validateTaxNumber]);

  useEffect(() => {
    return () => {
      activeValidationAbortRef.current?.abort();
    };
  }, []);

  return (
    <BlockStack gap="400">
      <FormLayout.Group>
        <Select
          label={t("companyDetails.taxType")}
          value={taxType}
          options={taxTypeOptions}
          onChange={(value) => handleInputChange(setTaxType, value ?? "")}
          disabled={isMetaLoading || disabled}
        />
        <TextField
          label={t("companyDetails.taxNumber")}
          value={taxNumber}
          onChange={(value) => handleInputChange(setTaxNumber, value)}
          autoComplete="off"
          requiredIndicator
          disabled={disabled}
          error={
            errors.taxNumber &&
            t("common.requiredField", {
              field_name: t("companyDetails.taxNumber"),
            })
          }
        />
        {validationState.isValidating && (
          <Banner tone="info">
            <p>{t("common.validating_please_wait")}</p>
          </Banner>
        )}
        
        {validationState.error && (
          <Banner tone="critical">
            <p>{validationState.error}</p>
          </Banner>
        )}
        
        {validationState.isValid && validationState.message && (
          <Banner tone="success">
            <p>{validationState.message}</p>
          </Banner>
        )}
        
      </FormLayout.Group>
    </BlockStack>
  );
};

export default TaxNumberComponent;
